import { Link } from "react-router-dom";
import { restaurants } from "@/mocks/restaurants";

interface SimilarRestaurantsProps {
  currentId: string;
  region: string;
  cuisine: string;
}

const priceColors: Record<string, string> = {
  "₺": "text-emerald-600",
  "₺₺": "text-amber-600",
  "₺₺₺": "text-orange-600",
  "₺₺₺₺": "text-rose-600",
};

const SimilarRestaurants = ({ currentId, region, cuisine }: SimilarRestaurantsProps) => {
  // Find similar restaurants: same region OR same cuisine, excluding current
  const similar = restaurants
    .filter((r) => r.id !== currentId && (r.region === region || r.cuisine === cuisine))
    .sort((a, b) => {
      // Prioritize same region AND same cuisine
      const aScore = (a.region === region ? 2 : 0) + (a.cuisine === cuisine ? 1 : 0);
      const bScore = (b.region === region ? 2 : 0) + (b.cuisine === cuisine ? 1 : 0);
      return bScore - aScore || b.rating - a.rating;
    })
    .slice(0, 4);

  if (similar.length === 0) return null;

  return (
    <div className="mt-14">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-1 h-6 bg-amber-500 rounded-full"></div>
        <h2
          className="text-xl font-bold text-stone-900"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Benzer Restoranlar
        </h2>
        <span className="text-xs text-stone-400 ml-1">
          {region} bölgesi &amp; {cuisine} mutfağı
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {similar.map((rest) => (
          <Link
            key={rest.id}
            to={`/restaurant/${rest.id}`}
            className="group bg-white border border-stone-100 rounded-2xl overflow-hidden hover:border-amber-200 hover:scale-[1.02] transition-all duration-200 block"
          >
            {/* Image */}
            <div className="relative h-36 overflow-hidden">
              <img
                src={rest.image}
                alt={rest.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              {/* Rating badge */}
              <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-2 py-0.5">
                <i className="ri-star-fill text-amber-400 text-xs"></i>
                <span className="text-white text-xs font-medium">{rest.rating}</span>
              </div>
              {/* Region match badge */}
              {rest.region === region && (
                <div className="absolute top-2 left-2 bg-amber-500/90 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  {rest.region}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="p-3">
              <h3
                className="font-bold text-stone-900 text-sm leading-snug mb-1 line-clamp-1 group-hover:text-amber-700 transition-colors"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {rest.name}
              </h3>
              <div className="flex items-center gap-1 text-stone-400 text-xs mb-2">
                <i className="ri-map-pin-line text-amber-400"></i>
                <span className="line-clamp-1">{rest.city}, {rest.district}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-stone-500 bg-stone-50 px-2 py-0.5 rounded-full">
                  {rest.cuisine}
                </span>
                <span className={`text-sm font-bold ${priceColors[rest.priceRange]}`}>
                  {rest.priceRange}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default SimilarRestaurants;
